import { Component, computed } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { ModalService } from 'src/app/services/gui/modal/modal.service';
import { UserService } from 'src/app/services/user.service';
import { trackByProperty } from '../../../services/global/trackingFunctions';

export type SettingsType = {
  name: string;
  value: any;
  isColumnSetting: boolean;
  isLanguageSetting: boolean;
  isViewSetting: boolean;
  parsed: any;
};

@Component({
  selector: 'sb-page-user-profile-settings',
  templateUrl: './user-profile-settings.component.html',
  styleUrls: ['./user-profile-settings.component.scss'],
  standalone: false,
})
export class UserProfileSettingsComponent {
  protected readonly trackByProperty = trackByProperty<SettingsType>;
  loading: boolean;

  debugMode = this.userService.debugMode;

  parsedUserSettings = computed(() => {
    const currentUserSettings = this.userService.currentUserSettings();
    if (!currentUserSettings) {
      return [];
    }
    return Object.entries(currentUserSettings).map(([key, value]) => this.parseSetting(key, value));
  });

  parsedUserClientSettings = computed(() => {
    const currentUserClientSettings = this.userService.currentUserClientSettings();
    if (!currentUserClientSettings) {
      return [];
    }
    return Object.entries(currentUserClientSettings).map(([key, value]) => this.parseSetting(key, value));
  });

  constructor(
    private readonly modal: ModalService,
    private readonly userService: UserService,
    public readonly translate: TranslateService
  ) {}

  public toggleDebugMode(): void {
    this.userService.setUserSetting('debugMode', (!this.debugMode).toString());
  }

  parseSetting(key: string, pValue: any): SettingsType {
    const ret = {
      name: key,
      value: pValue,
      isColumnSetting: key ? key.includes('ListSelectedColumnsV1') : false,
      isLanguageSetting: key ? key.includes('defaultLang') : false,
      isViewSetting: key ? key.includes('ViewSetting') : false,
      parsed: null,
    };
    try {
      ret.parsed = this.tryParseJSONObject(pValue);
    } catch (err) {}

    return ret;
  }

  tryParseJSONObject(json: any): any {
    try {
      const o = JSON.parse(json);

      if (o && typeof o === 'object') {
        return o;
      }
    } catch (e) {}

    return null;
  }

  clearUserSetting(name: string) {
    this.modal
      .confirm({
        yes: this.translate.instant('lang.globals.yes'),
        no: this.translate.instant('lang.globals.no'),
        title: this.translate.instant('lang.globals.deleteSetting'),
        message: this.translate.instant('lang.globals.deleteSettingQuestion.user'),
      })
      .then((res: boolean) => {
        if (res) {
          this.userService.clearUserSetting(name);
        }
      })
      .catch(() => {});
  }

  clearUserClientSetting(name: string) {
    this.modal
      .confirm({
        yes: this.translate.instant('lang.globals.yes'),
        no: this.translate.instant('lang.globals.no'),
        title: this.translate.instant('lang.globals.deleteSetting'),
        message: this.translate.instant('lang.globals.deleteSettingQuestion.userClient'),
      })
      .then((res: boolean) => {
        if (res) {
          this.userService.clearUserClientSetting(name);
        }
      })
      .catch(() => {});
  }

  factoryResetUserSettings() {
    this.modal
      .confirm({
        yes: this.translate.instant('lang.globals.yes'),
        no: this.translate.instant('lang.globals.no'),
        title: this.translate.instant('lang.globals.factoryReset'),
        message: this.translate.instant('lang.globals.factoryResetQuestion.user'),
      })
      .then((res: boolean) => {
        if (res) {
          this.userService.clearAllUserSettings();
        }
      })
      .catch(() => {});
  }

  factoryResetUserClientSettings() {
    this.modal
      .confirm({
        yes: this.translate.instant('lang.globals.yes'),
        no: this.translate.instant('lang.globals.no'),
        title: this.translate.instant('lang.globals.factoryReset'),
        message: this.translate.instant('lang.globals.factoryResetQuestion.userClient'),
      })
      .then((res: boolean) => {
        if (res) {
          this.userService.clearAllUserClientSettings();
        }
      })
      .catch(() => {});
  }

}
